import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { LanguageProvider } from "@shared/contexts/LanguageContext";
import { AuthProvider } from "@shared/contexts/AuthContext";
import { Toaster } from "@shared/components/ui/sonner";
import { createQueryClient } from "@shared/query/queryClient";

const queryClient = createQueryClient();
import LanguageSelect from "@visitor/features/language-select/LanguageSelect";
import NewQRInstructions from "@visitor/features/scan/QrInstructions";
import CodeEntry from "@visitor/features/scan/CodeEntry";
import ExhibitDetail from "@visitor/features/exhibit-detail/ExhibitDetail";
import ZoneDetail from "@visitor/features/zone-detail/ZoneDetail";
import ExhibitionDetail from "@visitor/features/exhibition-detail/ExhibitionDetail";
import History from "@visitor/features/history/History";
import StaffLogin from "@staff/features/auth/StaffLogin";
import StaffDashboard from "@staff/features/dashboard/StaffDashboard";
import ExhibitForm from "@staff/features/exhibits/ExhibitForm";
import ZoneDashboard from "@staff/features/zones/ZoneDashboard";
import ZoneForm from "@staff/features/zones/ZoneForm";
import ChangePassword from "@staff/features/auth/ChangePassword";
import VisitorStatistics from "@staff/features/stats/VisitorStatistics";
import AuditLog from "@staff/features/audit/AuditLog";
import UsersList from "@staff/features/users/UsersList";
import UserForm from "@staff/features/users/UserForm";
import OrgAdmin from "@staff/features/org/OrgAdmin";
import ExhibitionsList from "@staff/features/exhibitions/ExhibitionsList";
import AiUsageMeter from "@staff/features/settings/AiUsageMeter";
import ExhibitionForm from "@staff/features/exhibitions/ExhibitionForm";
import ExhibitionQRBoard from "@staff/features/exhibitions/ExhibitionQRBoard";
import AssetDetail from "@staff/features/assets/AssetDetail";
import ProtectedRoute from "./components/ProtectedRoute";

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <LanguageProvider>
          <BrowserRouter>
            <Routes>
              {/* Visitor Routes */}
              <Route path="/" element={<LanguageSelect />} />
              <Route path="/qr-instructions" element={<NewQRInstructions />} />
              {/* /code-entry: hidden from main flow (no UI link); reachable only by direct URL */}
              <Route path="/code-entry" element={<CodeEntry />} />
              <Route path="/exhibit/:id" element={<ExhibitDetail />} />
              <Route path="/zone/:id" element={<ZoneDetail />} />
              {/* F-B — visitor Exhibition detail (top tier). */}
              <Route path="/exhibition/:id" element={<ExhibitionDetail />} />
              <Route path="/history" element={<History />} />

              {/* Staff Routes */}
              <Route path="/staff/login" element={<StaffLogin />} />
              <Route
                path="/staff/dashboard"
                element={
                  <ProtectedRoute>
                    <StaffDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/exhibit/create"
                element={
                  <ProtectedRoute>
                    <ExhibitForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/exhibit/:id/edit"
                element={
                  <ProtectedRoute>
                    <ExhibitForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/zones"
                element={
                  <ProtectedRoute>
                    <ZoneDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/zone/create"
                element={
                  <ProtectedRoute>
                    <ZoneForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/zone/:id/edit"
                element={
                  <ProtectedRoute>
                    <ZoneForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/change-password"
                element={
                  <ProtectedRoute>
                    <ChangePassword />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/statistics"
                element={
                  <ProtectedRoute>
                    <VisitorStatistics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/audit"
                element={
                  <ProtectedRoute>
                    <AuditLog />
                  </ProtectedRoute>
                }
              />
              {/* F-A — user-management + org admin (admin-only; the
                  components themselves redirect non-admins). */}
              <Route
                path="/staff/users"
                element={
                  <ProtectedRoute>
                    <UsersList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/users/new"
                element={
                  <ProtectedRoute>
                    <UserForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/users/:id"
                element={
                  <ProtectedRoute>
                    <UserForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/org"
                element={
                  <ProtectedRoute>
                    <OrgAdmin />
                  </ProtectedRoute>
                }
              />
              {/* F-O — global AI narration usage meter (admin + editor;
                  the component redirects other roles). */}
              <Route
                path="/staff/ai-usage"
                element={
                  <ProtectedRoute>
                    <AiUsageMeter />
                  </ProtectedRoute>
                }
              />
              {/* F-B — exhibitions (admin / editor / analyst; scope filter
                  in the list query; non-scoped users see an empty list). */}
              <Route
                path="/staff/exhibitions"
                element={
                  <ProtectedRoute>
                    <ExhibitionsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/exhibition/create"
                element={
                  <ProtectedRoute>
                    <ExhibitionForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/exhibition/:id/edit"
                element={
                  <ProtectedRoute>
                    <ExhibitionForm />
                  </ProtectedRoute>
                }
              />
              {/* #4 — read-only QR board for an exhibition's full tree. */}
              <Route
                path="/staff/exhibition/:id/qr"
                element={
                  <ProtectedRoute>
                    <ExhibitionQRBoard />
                  </ProtectedRoute>
                }
              />
              {/* F-C — admin asset detail + purge (no nav entry; direct URL). */}
              <Route
                path="/staff/assets/:id"
                element={
                  <ProtectedRoute>
                    <AssetDetail />
                  </ProtectedRoute>
                }
              />
            </Routes>
            <Toaster position="top-center" />
          </BrowserRouter>
        </LanguageProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
